/* Rail-item de progression + toast de fin — voir hooks/use-bulk-import-jobs.tsx.
   Le rail-item est monté dans header.tsx (sidebar), le toast dans AppShell
   (visible sur toutes les pages). Même pattern exact que
   exam-import-progress.tsx (spec 13, PR #2285), sauf le toast, cliquable vers
   la session concernée (GO Ismaël 09/10). */

import { useEffect, useRef } from 'react';
import { CheckCircle, WarningCircle, X } from '@phosphor-icons/react';
import { Progress, ProgressIndicator, ProgressTrack } from './ui/progress';
import { useBulkImportJobs } from '../hooks/use-bulk-import-jobs';
import { useNavigate } from '../lib/routing';

const NOTICE_AUTO_DISMISS_MS = 5_000;

const STEP_LABEL: Record<string, string> = {
  generating: 'Analyse du document',
  saving: 'Enregistrement des copies',
  done: 'Terminé',
};

const FAILURE_KIND_LABEL: Record<string, string> = {
  appel_modele: "L'appel au modèle d'analyse a échoué.",
  json_invalide: "La réponse du modèle n'était pas exploitable.",
  invariant_page_partagee: 'Une page du PDF semble appartenir à deux copies différentes.',
  document_sans_session: "Ce document n'est plus rattaché à une session d'examen valide.",
  persistance: "L'enregistrement des copies détectées a échoué.",
  interrompu: 'Le traitement a été interrompu avant la fin.',
};

/* Rendu compact pour la sidebar gauche (header.tsx, à côté de
   ExamImportProgressRailItem) — même traitement que PR #2285. */
export function BulkImportProgressRailItem() {
  const { jobs } = useBulkImportJobs();
  const job = jobs.find((j) => j.status === 'running');
  if (!job) return null;

  const pct = job.pagesTotal > 0 ? Math.min(100, Math.round((job.copiesPersisted / job.pagesTotal) * 100)) : 0;
  const stepLabel = STEP_LABEL[job.step] ?? STEP_LABEL.generating;

  return (
    <div
      className="flex flex-col gap-1.5 mx-1 mb-2 px-3 py-2 rounded-[9px] bg-[var(--rail-surface)] border border-[var(--rail-line)]"
      role="status"
    >
      <span className="text-[12px] font-semibold text-[var(--rail-text-active)] truncate">{stepLabel}</span>
      <div className="flex items-center gap-2">
        <Progress value={pct} className="flex-1">
          <ProgressTrack>
            <ProgressIndicator className="bg-naia-blue" />
          </ProgressTrack>
        </Progress>
        <span className="text-[11.5px] font-semibold text-[var(--rail-text-muted)] tabular-nums shrink-0">{pct}%</span>
      </div>
    </div>
  );
}

export function BulkImportNoticeToast() {
  const { notice, dismissNotice } = useBulkImportJobs();
  const navigate = useNavigate();

  const noticeKey = notice ? `${notice.sessionId}:${notice.status}` : null;
  const shownKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!noticeKey || shownKeyRef.current === noticeKey) return;
    shownKeyRef.current = noticeKey;
    const timer = window.setTimeout(() => dismissNotice(), NOTICE_AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- dismissNotice est stable (useCallback sans deps) */
  }, [noticeKey]);

  useEffect(() => {
    if (!notice) shownKeyRef.current = null;
  }, [notice]);

  if (!notice) return null;

  const ok = notice.status === 'succeeded';
  const detail = !ok
    ? (notice.failureKind && FAILURE_KIND_LABEL[notice.failureKind]) || notice.error || "L'import a échoué."
    : null;

  const goToSession = () => {
    dismissNotice();
    navigate(`/teacher/exams/${notice.sessionId}`);
  };

  return (
    <div
      role="status"
      className="fixed inset-x-3 top-[calc(12px+env(safe-area-inset-top))] z-[60] mx-auto max-w-[420px] rounded-[14px] border border-border bg-card p-3 shadow-lg flex items-start gap-2 sm:inset-x-auto sm:right-[calc(12px+env(safe-area-inset-right))] sm:mx-0"
    >
      <button
        type="button"
        onClick={goToSession}
        className="flex-1 flex items-start gap-2 bg-transparent border-0 p-0 text-left cursor-pointer"
        aria-label="Voir la session d'examen concernée"
      >
        {ok ? (
          <CheckCircle size={20} weight="fill" className="shrink-0 text-[var(--naia-green-deep)]" />
        ) : (
          <WarningCircle size={20} weight="fill" className="shrink-0 text-[var(--naia-error)]" />
        )}
        <div className="flex-1 flex flex-col gap-0.5">
          <p className="m-0 text-[13.5px] font-bold text-[var(--naia-text-primary)]">
            {ok ? 'Import de copies terminé' : "Échec de l'import de copies"}
          </p>
          {detail && <p className="m-0 text-[12.5px] text-[var(--naia-text-secondary)]">{detail}</p>}
        </div>
      </button>
      <button
        type="button"
        onClick={dismissNotice}
        aria-label="Fermer"
        className="shrink-0 flex items-center justify-center size-[30px] rounded-full hover:bg-naia-sky"
      >
        <X size={16} weight="bold" />
      </button>
    </div>
  );
}
